export type VisitModality = 'in_person' | 'video' | 'audio' | null;

export interface ResolvedVisitMode {
  modality: VisitModality;
}

/**
 * Single source of truth for turning the stored visit-mode fields (modality +
 * telehealth) into the modality that should be shown/used.
 *
 * Telehealth means "virtual visit" — either video or audio (phone). It is kept
 * in lockstep with modality on write (only in-person clears it). On read, an
 * explicit modality always wins, so an audio telehealth visit stays audio and an
 * explicit video stays video — including the CUS-753 legacy case where
 * default_modality was saved as 'video' but telehealth was never flipped. Only
 * when no modality is stored does the telehealth flag imply a video default.
 */
export const resolveVisitMode = (
  modality: VisitModality,
  telehealth: boolean,
): ResolvedVisitMode => ({
  modality: modality ?? (telehealth ? 'video' : null),
});

/** Display label for a visit mode. */
export const formatVisitModeLabel = (modality: VisitModality, telehealth: boolean): string => {
  const { modality: resolved } = resolveVisitMode(modality, telehealth);
  if (resolved === 'video') return 'Video';
  if (resolved === 'audio') return 'Phone';
  if (resolved === 'in_person') return 'In-person';
  return 'Visit mode not set';
};
